<script setup lang="ts">
import { RouterLink } from 'vue-router';

export type Item = {
  name: string;
  label: string;
  status: 'past' | 'current' | 'next' | 'future';
  hasActiveChild: boolean;
  children: {
    name: string;
    label: string;
    isActive: boolean;
  }[];
};

defineProps<{
  items: Item[];
}>();
</script>

<template>
  <div class="flex flex-col">
    <template v-for="(item, itemIndex) in items" :key="item.name">
      <div class="flex space-x-1 items-center">
        <div
          class="flex-0 flex w-[20px] h-[20px] rounded-full relative border-[6px]"
          :class="{
            'bg-blue-600 border-blue-600': item.status === 'past',
            'bg-white border-blue-600': item.status === 'current',
            'bg-white border-gray-400': item.status === 'next',
            'bg-white border-gray-200': item.status === 'future',
            'before:absolute before:top-[-11px] before:left-[3px] before:h-[5px] before:w-[1px]':
              itemIndex > 0,
            'after:absolute after:bottom-[-11px] after:left-[3px] after:h-[5px] after:w-[1px]':
              itemIndex < items.length - 1,
            'before:bg-blue-600': itemIndex > 0 && item.status !== 'future',
            'after:bg-blue-600':
              itemIndex < items.length - 1 &&
              (item.status === 'past' || item.status === 'current'),
            'after:bg-gray-400':
              itemIndex < items.length - 1 && item.status === 'next',
            'after:bg-gray-200':
              itemIndex < items.length - 1 && item.status === 'future',
            'before:bg-gray-400':
              itemIndex > 0 &&
              item.status === 'future' &&
              items[itemIndex - 1].status === 'next',
            'before:bg-gray-200':
              itemIndex > 0 &&
              item.status === 'future' &&
              items[itemIndex - 1].status === 'future',
          }"
        >
          <div
            v-if="item.status === 'current'"
            class="bg-blue-600 w-[8px] h-[8px] rounded-full border-[1px] border-white"
          />
        </div>
        <RouterLink
          class="flex-1 items-center p-[6px] h-[30px] hover:bg-blue-50 hover:text-blue-600 rounded-md"
          :class="{
            'bg-blue-50 text-blue-600': item.status === 'current',
          }"
          :to="{ name: item.name }"
        >
          {{ item.label }}
        </RouterLink>
      </div>
      <template v-if="item.hasActiveChild">
        <RouterLink
          v-for="child in item.children"
          :key="child.name"
          class="ml-[9px] block border-l border-blue-600 pl-[20px] pr-3 pt-8 pb-4 hover:text-blue-600"
          :class="{
            'text-blue-600': child.isActive,
          }"
          :to="{ name: child.name }"
        >
          {{ child.label }}
        </RouterLink>
      </template>
      <div
        v-else-if="itemIndex < items.length - 1"
        class="ml-[9px] w-[1px] h-[20px]"
        :class="{
          'bg-blue-600': item.status === 'past' || item.status === 'current',
          'bg-gray-400': item.status === 'next',
          'bg-gray-200': item.status === 'future',
        }"
      />
    </template>
  </div>
</template>
